/**
 * Modal Add to Cart Handler
 * Handles add-to-cart functionality for products in modals/quickshop
 * Works independently from empire.js
 * Uses MutationObserver and capture phase to intercept all clicks
 */

(function() {
  'use strict';

  function ModalATCHandler() {
    this.observer = null;
    this.attachedButtons = new WeakSet();
    this.init();
  }

  ModalATCHandler.prototype = {
    init: function() {
      console.log('Modal ATC Handler: Initializing');
      // Listen for the quickshop:opened event from empire.js
      this.listenForModalOpen();
    },

    listenForModalOpen: function() {
      // Listen for the legitimate quickshop:opened event dispatched from empire.js ProductQuickshop._open()
      window.addEventListener('quickshop:opened', () => {
        console.log('Modal ATC Handler: Quickshop opened, waiting for content to load');
        // Wait for modal content to be fully rendered with retry mechanism
        this.waitForModalContent();
      });
    },

    waitForModalContent: function(attempt = 0) {
      const maxAttempts = 20; // Max 2 seconds (20 * 100ms)
      
      // Check for modal container first
      const modalContainer = document.querySelector('[data-modal-container]');
      if (!modalContainer) {
        console.log('Modal ATC Handler: No modal container found yet (attempt', attempt + 1, '/', maxAttempts, ')');
      }
      
      const modalForms = document.querySelectorAll('[data-modal-container] form[action="/cart/add"]');
      
      if (modalForms.length > 0) {
        console.log('Modal ATC Handler: Modal content loaded, found', modalForms.length, 'forms');
        this.checkAndAttach();
      } else if (attempt < maxAttempts) {
        // Modal content not ready yet, try again
        setTimeout(() => {
          this.waitForModalContent(attempt + 1);
        }, 100);
      } else {
        console.warn('Modal ATC Handler: Timeout waiting for modal content after', maxAttempts, 'attempts');
      }
    },

    checkAndAttach: function() {
      // Find all product forms in modals
      const modalForms = document.querySelectorAll('[data-modal-container] form[action="/cart/add"]');
      console.log('Modal ATC Handler: Found', modalForms.length, 'modal forms');
      
      let attachedCount = 0;
      let skippedCount = 0;
      
      modalForms.forEach((form) => {
        const atcButton = form.querySelector('[data-product-atc]');
        if (atcButton) {
          if (!this.attachedButtons.has(atcButton)) {
            this.attachHandler(atcButton, form);
            attachedCount++;
          } else {
            skippedCount++;
          }
        } else {
          console.warn('Modal ATC Handler: Form found but no [data-product-atc] button', form);
        }
      });
      
      console.log('Modal ATC Handler: Attached', attachedCount, 'new buttons, skipped', skippedCount, 'already attached');
    },

    attachHandler: function(button, form) {
      // Mark this button as handled
      this.attachedButtons.add(button);
      
      console.log('Modal ATC Handler: Attaching to button');
      
      // Use capture phase with highest priority
      // This runs BEFORE any other click handlers
      button.addEventListener('click', (e) => {
        this.handleATCClick(e, form, button);
      }, true); // true = capture phase, runs first
    },

    handleATCClick: function(e, form, button) {
      // Stop ALL event propagation - this prevents any other handlers from running
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Check if button is disabled
      if (button.disabled || button.classList.contains('disabled')) {
        return;
      }

      // Prevent double submissions
      if (button.classList.contains('loading')) {
        return;
      }

      // Get form data
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity') || 1;

      if (!variantId) {
        this.showError('Please select a variant');
        return;
      }

      // Add loading state
      button.classList.add('loading');
      button.disabled = true;

      // Prepare data for cart add
      const data = {
        id: variantId,
        quantity: parseInt(quantity, 10),
      };

      // Add any additional form fields (properties, selling plan, etc.)
      for (let [key, value] of formData.entries()) {
        if (key.startsWith('properties[')) {
          if (!data.properties) {
            data.properties = {};
          }
          const propName = key.match(/properties\[(.*?)\]/)[1];
          data.properties[propName] = value;
        } else if (key === 'selling_plan') {
          data.selling_plan = value;
        }
      }

      // Add to cart via AJAX
      this.addToCart(data, button, form);
    },

    addToCart: function(data, button, form) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => Promise.reject(err));
        }
        return response.json();
      })
      .then(item => {
        this.handleSuccess(item, button, form);
      })
      .catch(error => {
        this.handleError(error, button);
      });
    },

    handleSuccess: function(item, button, form) {
      // Remove loading state
      button.classList.remove('loading');
      button.disabled = false;

      // Update cart count if there's a cart drawer
      this.updateCartCount();

      // Get cart redirection setting
      const quickBuySettings = document.querySelector('[data-quick-buy-settings]');
      let cartRedirection = false;
      
      if (quickBuySettings) {
        try {
          const settings = JSON.parse(quickBuySettings.textContent);
          cartRedirection = settings.cart_redirection;
        } catch (e) {
          console.warn('Could not parse quick buy settings');
        }
      }

      // Check global settings as fallback
      if (typeof window.theme !== 'undefined' && window.theme.settings) {
        cartRedirection = cartRedirection || window.theme.settings.cart_redirection;
      }

      if (cartRedirection) {
        // Redirect to cart page
        window.location.href = '/cart';
      } else {
        // Show success message and/or open cart drawer
        this.showSuccessMessage(item);
        this.openCartDrawer();
        
        // Close the modal after a short delay
        setTimeout(() => {
          this.closeModal();
        }, 500);
      }
    },

    handleError: function(error, button) {
      // Remove loading state
      button.classList.remove('loading');
      button.disabled = false;

      // Show error message
      let errorMessage = 'Unable to add item to cart';
      
      if (error && error.description) {
        errorMessage = error.description;
      } else if (error && error.message) {
        errorMessage = error.message;
      }

      this.showError(errorMessage);
    },

    showSuccessMessage: function(item) {
      // Try to show success in product quickshop message area
      const messageContainer = document.querySelector('[data-product-quickshop-message]');
      if (messageContainer) {
        messageContainer.innerHTML = `
          <div class="product-message product-message--success">
            <span>Item added to cart!</span>
          </div>
        `;
        messageContainer.style.display = 'block';
      }

      // Dispatch custom event for other scripts to listen to
      window.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item: item }
      }));
    },

    showError: function(message) {
      const messageContainer = document.querySelector('[data-product-quickshop-message]');
      if (messageContainer) {
        messageContainer.innerHTML = `
          <div class="product-message product-message--error">
            <span>${message}</span>
          </div>
        `;
        messageContainer.style.display = 'block';
      } else {
        // Fallback to alert if no message container
        alert(message);
      }
    },

    updateCartCount: function() {
      // Dispatch the cartcount:update event that cart-drawer.js listens for
      // This will trigger fetchAndRenderCartDrawerSection() which updates and opens the drawer
      window.dispatchEvent(new Event('cartcount:update'));
      
      // Also fetch cart data to update count displays
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // Update cart count in header
          const cartCounts = document.querySelectorAll('[data-cart-count]');
          cartCounts.forEach(el => {
            el.textContent = cart.item_count;
          });

          // Dispatch additional event for other scripts
          window.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cart }
          }));
        })
        .catch(error => {
          console.warn('Could not update cart count:', error);
        });
    },

    openCartDrawer: function() {
      // Cart drawer will already be opened by the cartcount:update event
      // dispatched in updateCartCount(), which triggers fetchAndRenderCartDrawerSection()
      // No additional action needed here
    },

    closeModal: function() {
      // Find and click modal close button
      const modalClose = document.querySelector('[data-modal-close]');
      if (modalClose) {
        modalClose.click();
      }
    }
  };

  // Initialize when script loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      new ModalATCHandler();
    });
  } else {
    new ModalATCHandler();
  }

  // Make available globally if needed
  window.ModalATCHandler = ModalATCHandler;

})();

